import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

export interface WeeklyEntry {
  week_start: string;
  total_sent: number;
  total_opened: number;
  open_rate: number;
  attributed_visits: number;
}

export interface BusinessEntry {
  business_id: string;
  business_name: string;
  total_sent: number;
  total_opened: number;
  open_rate: number;
  attributed_visits: number;
  weekly: WeeklyEntry[];
}

export interface NotificationMetricsData {
  summary: {
    total_sent: number;
    total_opened: number;
    open_rate: number;
    total_attributed_visits: number;
  };
  weekly: WeeklyEntry[];
  by_business: BusinessEntry[];
}

const EMPTY_DATA: NotificationMetricsData = {
  summary: { total_sent: 0, total_opened: 0, open_rate: 0, total_attributed_visits: 0 },
  weekly: [],
  by_business: [],
};

export type WeeksFilter = number | 'all';

export function useNotificationMetrics(weeks: WeeksFilter, apiUrl: string): {
  data: NotificationMetricsData;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [data, setData] = useState<NotificationMetricsData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);

  const refetch = useCallback(() => setTrigger((t) => t + 1), []);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    let isMounted = true;

    const fetchData = async () => {
      if (!apiUrl) {
        if (isMounted) { setError('API URL is not configured for this environment'); setLoading(false); }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };

        const res = await fetch(
          `${apiUrl}/admin/b2b/notification-weekly-stats?weeks=${weeks}`,
          { headers },
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);

        if (isMounted) {
          setData(json);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Failed to load notification metrics');
          setData(EMPTY_DATA);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance, accounts, weeks, apiUrl, trigger]);

  return { data, loading, error, refetch };
}
